import { useState } from 'react';
import moment from 'moment';
import { Drawer } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../../store/useUserStore';
import {
  useFrequentQnaCategoryQuery,
  useFrequentQnaListQuery,
  useFrequentQnaDetailQuery,
} from '../../../queries';
import Text from '../../../component/common/Text';
import IcRightArrow from '../../../assets/image/ic_chevron_right.svg';
import { stripHtmlTags } from '../../../utility/stripHtmlTag';
import FullHeightContainer from '../../../component/common/FullHeightContainer';
import TopHeaderWithPrev from '../../../component/common/TopHeaderWithPrev';
import UnderlineTitle from '../../../component/common/UnderlineTitle';
import { useTranslation } from '../../../hook/useTranslation';
import Url from '../../../data/url';

function MyFrequentQnAPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useUserStore();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [noticeOpenId, setNoticeOpenId] = useState<number | null>(null);

  const { data: categoryData } = useFrequentQnaCategoryQuery();
  const categoryList = categoryData?.categories || [];

  const { data: qnas = [] } = useFrequentQnaListQuery();

  const { data: qnaDetail = null } = useFrequentQnaDetailQuery(noticeOpenId as number, {
    enabled: noticeOpenId !== null,
  });

  return (
    <FullHeightContainer>
      <TopHeaderWithPrev title={t('my.faq.title')} onClickPrev={() => navigate(Url.My)} />
      <div className="pageInner bg-[#FFF]">
        <div className="w-full flex flex-col items-start justify-center my-4">
          <div className="text-[24px] font-medium">
            <UnderlineTitle title={user?.nickName || user?.name || ''} />
            {t('my.faq.question_suffix')}
          </div>
          <div className="text-[24px] font-medium">{t('my.faq.what_curious')}</div>
        </div>
        <div className="w-full flex gap-2 justify-start items-center overflow-x-auto whitespace-nowrap pb-2">
          {categoryList?.length > 0 &&
            categoryList?.map((type) => {
              return (
                <button
                  key={type}
                  type="button"
                  className="px-3 py-1 mb-2 text-[13px] border border-[#EAEAEA] rounded-full shrink-0"
                  onClick={() => {
                    setSelectedCategory(type);
                    setNoticeOpenId(null);
                  }}
                  style={{
                    backgroundColor: selectedCategory === type ? '#F0FCFC' : 'transparent',
                    color: selectedCategory === type ? '#00B4A0' : '#333333',
                    borderColor: selectedCategory === type ? '#00B4A0' : '#EAEAEA',
                    fontWeight: selectedCategory === type ? 'bold' : 'normal',
                  }}
                >
                  {type}
                </button>
              );
            })}
        </div>
        {qnas
          .filter((qna) => {
            if (selectedCategory) {
              return qna.category === selectedCategory;
            }
            return true;
          })
          .map((notice) => {
            return (
              <button
                key={notice.id}
                type="button"
                className="w-full flex flex-col items-start justify-start p-4 pl-0 border-b border-gray-200 hover:bg-gray-50"
                onClick={() => setNoticeOpenId(notice.id)}
              >
                <div className="w-full flex items-center justify-between mb-2">
                  <div className="font-normal text-[12px] text-[#767676]">{notice?.category}</div>
                  <div className="font-normal text-[12px] text-[#767676]">
                    {moment(notice?.updatedAt).format('YYYY.MM.DD')}
                  </div>
                </div>
                <div className="w-full flex items-center justify-between">
                  <div className="text-left">
                    <div className="font-normal text-[14px]">{notice?.title}</div>
                  </div>
                  <img src={IcRightArrow} alt="chevron right" />
                </div>
              </button>
            );
          })}
      </div>
      <Drawer
        open={noticeOpenId !== null}
        onClose={() => setNoticeOpenId(null)}
        anchor="right"
        sx={{
          '& .MuiDrawer-paper': {
            width: '100vw',
            maxWidth: '100vw',
          },
        }}
      >
        <TopHeaderWithPrev title="" onClickPrev={() => setNoticeOpenId(null)} />
        <FullHeightContainer>
          <div className="pageInner bg-[#FFF]">
            <div className="w-full">
              <div className="w-full py-5">
                <div className="text-[18px] font-bold">{qnaDetail?.title}</div>
                <div className="text-[12px] font-normal text-[#767676]">
                  {moment(qnaDetail?.updatedAt).format('YYYY.MM.DD')}
                </div>
              </div>
              <Text text={qnaDetail?.content ? stripHtmlTags(qnaDetail.content) : ''} />
              <div className="w-full h-5" />
            </div>
          </div>
        </FullHeightContainer>
      </Drawer>
    </FullHeightContainer>
  );
}

export default MyFrequentQnAPage;
